import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../context/AuthContext';
import Login from '../pages/login/login';
import { PrivateRoute } from './PrivateRoute';
import useMsal from '../auth/useMsal';
import ReportEventsComponent from '../pages/report-events/ReportEvents';
import PageHeader from '../components/page-header/PageHeader';
import SidePanelLeft from '../components/common/side-panel/SidePanelLeft';
import useSetData from '../app/useSetData';
import { useVesselStore } from '../context/VesselContext';
import SpinnerLoader from '../components/common/loader/SpinnerLoader';
import { useRotationStore } from '../context/RotationContext';
import SelectVesselPage from '../pages/select-vessel/SelectVessel';
import { AppWrapper, PageWrapper, ContentWrapper } from '../App.styles';
import useGetRole from '../api/useGetRole';
import useGetVessels from '../api/useGetVessels';
import { useAppStore } from '../context/AppContext';
import { useAolStore } from '../context/AolContext';
import useGetAppsMenu from '../api/useGetAppsMenu';
import SidePanelPopup from '../components/common/side-panel-popup/SidePanelPopup';
import { SidePanelModulEnum } from '../enums/sidePanelModulEnum';
import queryParamsUtils from '../utils/queryParamsUtils';
import TankCleaningComponent from '../pages/tank-cleaning/TankCleaning';
import { AppFeatureEnum } from '../enums/appFeatureEnum';
import useGetAppFeatures from '../api/useGetAppFeatures';
import useAppFeature from '../app/useAppFeature';
import useApiLoaderHelper from '../utils/useApiLoaderHelper';
import { useSWService } from '../api/sw/useSWService';
import useGetUserSettings from '../api/useUserSettingsApi';
import { useEventStore } from '../context/EventContext';
import CargoTempLogComponent from '../pages/cargo-temp-log/cargoTempLog';
import useUpdateMissingTasksIcon from '../app/useUpdateMissingTasksIcon';
import VerificationComponent from '../pages/verification/Verification';
import PBIReportWrapper from '../pages/pbi-report/PBIReportWrapper';
import { useEventTypeStore } from '../context/EventTypeContext';
import { useTankCleaningStore } from '../context/TankCleaningContext';
import AolComponent from 'src/pages/aol/aol';
import useGetPBIAccessToken from 'src/api/useGetPBIAccessToken';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';
import { useCargoTempLogStore } from 'src/context/CargoTempLogContext';
import StowagePage from 'src/pages/stowage/StowagePage';
import BunkeringPage from 'src/pages/bunkering/BunkeringPage';
import { useStowageStore } from 'src/context/StowageContext';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import useCheckConnection from 'src/utils/connectivity/useCheckConnection';
import AppModalComponent from 'src/components/appModal/AppModal';
import { getAolGroupDate } from 'src/utils/aol/aolDateUtils';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import Unauthorized from '../pages/unauthorized/Unauthorized';
import { useBunkerContext } from 'src/context/BunkerContext';

const RouterContent = () => {
    const { checkAuthStatus, initHandleRedirect, handleAppAccessToken, isInitialized } = useMsal();
    const { loggedInUser, isAuthenticating, isVerifyingToken, role } = useAuthStore();
    const [, setIsAuthorized] = useState<boolean | undefined>(undefined);
    const location = useLocation();
    const { getRole } = useGetRole();
    const { getAppFeatures } = useGetAppFeatures();
    const { getVessels } = useGetVessels();
    const { getAppsMenuHtml } = useGetAppsMenu();
    const { isFeatureEnabled } = useAppFeature();
    const { selectedVessel, setSelectedVessel, isVesselListRetrieved, vesselList } = useVesselStore();
    const { visibleSidePanelModuleIndex, appModalData } = useAppStore();
    const { setRotation } = useRotationStore();
    const { hasAuthorizedAppToken } = queryParamsUtils();
    const { checkLatency } = useCheckConnection();
    const navigate = useNavigate();
    const { getVisibleLoaderMessage } = useApiLoaderHelper();
    const { setupServiceWorker } = useSWService();
    const { getUserSettings } = useGetUserSettings();
    const { updateAllMissingTasks } = useUpdateMissingTasksIcon();
    const { setSelectedEvent, setFullEventArray, setDeltaEventArray, setEventDataForVoyageList } = useEventStore();
    const { setSelectedEventType } = useEventTypeStore();
    const { getPbiAccessToken } = useGetPBIAccessToken();
    const {
        setUpdatedRotation,
        setCurrentPortCall,
        setNextPortCall,
        setIsRotationFilterActive,
        setIsFilterPanelOpen,
        setSelectedNode,
        setSelectedStowageVoyages,
        selectedStowageRotationPortCall,
    } = useRotationStore();
    const { triggerQueryParamsUpdate } = useQueryParamsHandler();
    const {
        setSelectedTankCleaningPortCall,
        setSelectedChemicalRobForPortCall,
        setSelectedChemicalOrderForPortCall,
        setSelectedChemicalOrderForPortCallList,
        setChemicalOrderUpdateReportData,
        setChemicalRobForPortCallList,
        setChemicalOrderForPortCallList,
    } = useTankCleaningStore();
    const { setCargoTempLogDates, setCargoTemperatureConfigs, setFilteredCargoTemperatureConfigs } = useCargoTempLogStore();
    const {
        aolGroupStatusMessage,
        setSelectedAolGroup,
        selectedAolGroup,
        setSelectedAolDataItem,
        setAolGroups,
        setAolDataItems,
        setAolItems,
        setNoonReports,
        setAolEventsArray,
    } = useAolStore();
    const {
        setMissingCargoTempLogStatuses,
        setMissingRobStatuses,
        setMissingNoonReportStatuses,
        setNumMissingBunkerRegistrations,
        setNumMissingBunkerRob,
        setIsVoyageSelectionPanelOpen,
    } = useAppStore();
    const {
        setStowagePlanRotationPortCallList,
        setStowagePlanRotationCargoList,
        setSelectedStowageRotationBerthCall,
        setSelectedStowageRotationPortCall,
        setImosRotationPortCallList,
        setImosRotationCargoList,
        setStowageRotationTree,
    } = useRotationStore();
    const {
        setTankPlan,
        setStowagePlans,
        setActiveStowagePlan,
        setIsStowagePlanUpdated,
        setStowageModalData,
        setLoadedMasterStowagePlan,
        setImosSyncChanges,
        setMasterPlanSyncData,
        setStowagePlanChanges,
    } = useStowageStore();
    const { setSelectedCargoOperation } = useStowageSelectionStore();
    const { setViolations } = useViolationsStore();
    const { setBunkerings, setBunkeringEvents, setSelectedBunkering, setSelectedBunkeringEvent } = useBunkerContext();

    useSetData();

    const { queryParamUpdate, triggerAllQueryParamsFromUrl, getQueryParamValueFromUrl } = useQueryParamsHandler();
    // ============= USE EFFECTS ================

    useEffect(() => {
        triggerAllQueryParamsFromUrl();
        resetContextOnLocationChange();
    }, [location]);

    useEffect(() => {
        if (loggedInUser && queryParamUpdate && isVesselListRetrieved) {
            if (location.pathname !== '/select-vessel') {
                selectShipByImoNumber(loggedInUser.appAccessToken);
            }
        }
    }, [queryParamUpdate, loggedInUser, isVesselListRetrieved]);
    useEffect(() => {
        if (loggedInUser) {
            setIsAuthorized(true);
            getRole();
            getUserSettings();
            getVessels();
            getAppFeatures();
            getAppsMenuHtml();
            getPbiAccessToken();
        } else if (!hasAuthorizedAppToken() && isInitialized) {
            // Only call checkAuthStatus after MSAL is initialized
            checkAuthStatus();
        }
    }, [loggedInUser, isInitialized]);
    useEffect(() => {
        setupServiceWorker();
        // Only call MSAL functions after initialization is complete
        if (isInitialized) {
            if (!hasAuthorizedAppToken()) {
                initHandleRedirect();
                checkAuthStatus();
            } else {
                handleAppAccessToken();
            }
        }
    }, [isInitialized]);

    useEffect(() => {
        if (selectedVessel) {
            document.title =
                `${selectedVessel.vesselName}` +
                (location.pathname.startsWith('/stowage') && selectedStowageRotationPortCall
                    ? ' Stowage ' + selectedStowageRotationPortCall.voyageNumber
                    : '') +
                (location.pathname.startsWith('/aol') && selectedAolGroup ? ' AOL ' + prettifyDate(getAolGroupDate(selectedAolGroup)) : '');
        }
    }, [location, selectedVessel, selectedStowageRotationPortCall, selectedAolGroup]);

    const visibleLoaderMessage = getVisibleLoaderMessage(true);

    const setLocalStorage = (imoNumber: number) => {
        localStorage.setItem('selectedShipImoNumber', JSON.stringify(imoNumber));
    };
    const selectShipByImoNumber = (appAccessToken?: string) => {
        const url_imoNumber = getQueryParamValueFromUrl('imoNumber');
        const ls_imoNumber = localStorage.getItem('selectedShipImoNumber');

        if (url_imoNumber && selectedVessel && +url_imoNumber === selectedVessel.imoNumber) {
            return;
        }

        if (!url_imoNumber && !ls_imoNumber && !selectedVessel) {
            navigate('/select-vessel');
            return;
        }

        if (!url_imoNumber && ls_imoNumber) {
            const queryParamValues: QueryParamValueProps[] = [{ name: 'imoNumber', newValue: ls_imoNumber.toString() }];
            triggerQueryParamsUpdate(queryParamValues, 'clear-all');
            return;
        }

        if (appAccessToken) {
            const queryParamValues: QueryParamValueProps[] = [{ name: 'appAccessToken', newValue: appAccessToken }];
            triggerQueryParamsUpdate(queryParamValues);
        }

        if (url_imoNumber) {
            const vessel = vesselList.find((v) => v.imoNumber.toString() === url_imoNumber);
            if (vessel) {
                setSelectedVessel(vessel);
                setLocalStorage(+url_imoNumber);
                resetContext();

                checkLatency(vessel.imoNumber, -1, -1);

                if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
                    updateAllMissingTasks(vessel.imoNumber);
                }
            }
        }

        return;
    };

    const resetContextOnLocationChange = () => {
        setSelectedStowageVoyages(null);
        setIsVoyageSelectionPanelOpen(null);
    };

    const resetContext = () => {
        setRotation(null);
        setFullEventArray(null);
        setAolEventsArray(null);
        setDeltaEventArray(null);
        setSelectedNode(null);
        setCurrentPortCall(null);
        setNextPortCall(null);
        setSelectedStowageVoyages(null);
        setIsRotationFilterActive(false);
        setIsFilterPanelOpen(false);
        setUpdatedRotation(null);
        setSelectedEvent(null);
        setSelectedEventType(null);
        setEventDataForVoyageList(null);

        // CONSUMABLES
        setSelectedTankCleaningPortCall(null);
        setSelectedChemicalRobForPortCall(null);
        setChemicalRobForPortCallList(null);
        setChemicalOrderForPortCallList(null);
        setSelectedChemicalOrderForPortCall(null);
        setSelectedChemicalOrderForPortCallList(null);
        setChemicalOrderUpdateReportData(null);

        //CARGO TEMPERATURE LOG
        setNoonReports(null);
        setCargoTempLogDates(null);
        setCargoTemperatureConfigs(null);
        setFilteredCargoTemperatureConfigs([]);

        setMissingNoonReportStatuses(null);
        setMissingRobStatuses(null);
        setMissingCargoTempLogStatuses(null);
        setNumMissingBunkerRegistrations(0);
        setNumMissingBunkerRob(0);

        //AOL
        setSelectedAolDataItem(null);
        setSelectedAolGroup(null);
        setAolItems(null);
        setAolDataItems([]);
        setAolGroups([]);

        //STOWAGE
        setLoadedMasterStowagePlan(null);
        setStowagePlanRotationPortCallList(null);
        setStowagePlanRotationCargoList(null);
        setTankPlan(null);
        setStowagePlans(null);
        setActiveStowagePlan(null);
        setViolations(null);
        setSelectedStowageRotationPortCall(null);
        setSelectedStowageRotationBerthCall(null);
        setSelectedCargoOperation(null);
        setImosRotationPortCallList(null);
        setImosRotationCargoList(null);
        setIsStowagePlanUpdated(null);
        setStowageModalData(null);
        setStowageRotationTree(null);
        setStowagePlanChanges([]);
        setImosSyncChanges([]);
        setMasterPlanSyncData(null);

        // BUNKERING
        setSelectedBunkering(null);
        setSelectedBunkeringEvent(null);
        setBunkerings([]);
        setBunkeringEvents([]);
    };

    const RootRoute = () => {
        const windowParams = new URLSearchParams(window.location.search);
        return <Route path="/" element={<Navigate to={{ pathname: '/login', search: `?${windowParams}` }} />} />;
    };

    const LoginRoute = () => {
        const windowParams = new URLSearchParams(window.location.search);
        if (hasAuthorizedAppToken() && loggedInUser) {
            return <Route path="/login" element={<Navigate to={{ pathname: '/report-events', search: `?${windowParams}` }} />} />;
        } else if (loggedInUser) {
            return <Route path="/login" element={<Navigate to={{ pathname: '/select-vessel ', search: `?${windowParams}` }} />} />;
        }
        return <Route path="/login" element={<Login />} />;
    };

    const SelectShipRoute = () => {
        if (selectedVessel) {
            const windowParams = new URLSearchParams(window.location.search);
            windowParams.set('imoNumber', selectedVessel.imoNumber.toString());
            return <Route path="/select-vessel" element={<Navigate to={{ pathname: 'report-events', search: `?${windowParams}` }} />} />;
        }
        return <Route path="/select-vessel" element={<SelectVesselPage />} />;
    };

    const EnableCargoConsumablesRoute = () => {
        if (selectedVessel && !selectedVessel.hasOrcaWebSimpleMode) {
            const appFeature = { featureEnum: AppFeatureEnum.CargoConsumable, date: new Date() };
            if (isFeatureEnabled(appFeature)) {
                return (
                    <Route
                        path="/tank-cleaning"
                        element={
                            <PrivateRoute>
                                <TankCleaningComponent />
                            </PrivateRoute>
                        }
                    />
                );
            }
        }
    };

    const EnableVerificationRoute = () => {
        if (role) {
            const appFeature = { featureEnum: AppFeatureEnum.Verification, date: new Date(), role: role.roleType };
            if (isFeatureEnabled(appFeature)) {
                return (
                    <Route
                        path="/verification"
                        element={
                            <PrivateRoute>
                                <VerificationComponent />
                            </PrivateRoute>
                        }
                    />
                );
            }
        }
    };

    const CargoTempLogRoute = () => {
        if (selectedVessel && !selectedVessel.hasOrcaWebSimpleMode) {
            const appFeature = {
                featureEnum: AppFeatureEnum.CargoTemperatureControlLog,
                date: new Date(),
            };
            if (isFeatureEnabled(appFeature)) {
                return (
                    <Route
                        path="/cargo-temp-log"
                        element={
                            <PrivateRoute>
                                <CargoTempLogComponent />
                            </PrivateRoute>
                        }
                    />
                );
            }
        }
    };

    const PBIReportRoute = () => {
        const appFeature = { featureEnum: AppFeatureEnum.PbiReports, date: new Date() };
        if (isFeatureEnabled(appFeature)) {
            return (
                <Route
                    path="/dashboards"
                    element={
                        <PrivateRoute>
                            <PBIReportWrapper />
                        </PrivateRoute>
                    }
                />
            );
        }
    };

    const StowageRoute = () => {
        const appFeature = { featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber };
        if (isFeatureEnabled(appFeature) && hasPermission(role, stowageFeatureEnum.ViewStowagePlan)) {
            return (
                <Route
                    path="/stowage"
                    element={
                        <PrivateRoute>
                            <StowagePage />
                        </PrivateRoute>
                    }
                />
            );
        }
    };
    const BunkeringRoute = () => {
        const appFeature = { featureEnum: AppFeatureEnum.Bunkering, date: new Date(), imoNumber: selectedVessel?.imoNumber };
        if (isFeatureEnabled(appFeature)) {
            return (
                <Route
                    path="/bunkering"
                    element={
                        <PrivateRoute>
                            <BunkeringPage />
                        </PrivateRoute>
                    }
                />
            );
        }
    };

    const UnauthorizedRoute = () => {
        if (role) {
            return <Route path="/unauthorized" element={<Navigate to={{ pathname: '/' }} />} />;
        }
        return <Route path="/unauthorized" element={<Unauthorized />} />;
    };
    return (
        <AppWrapper>
            <AppModalComponent />
            {(visibleLoaderMessage || aolGroupStatusMessage) && !appModalData && (
                <SpinnerLoader showOverlay={true} message={visibleLoaderMessage ?? aolGroupStatusMessage ?? ''} />
            )}
            <PageHeader />
            {!isVerifyingToken && !isAuthenticating && (
                <PageWrapper>
                    {selectedVessel && selectedVessel.imoNumber && <SidePanelLeft />}
                    <ContentWrapper>
                        <Routes>
                            {RootRoute()}
                            {LoginRoute()}
                            {SelectShipRoute()}
                            {UnauthorizedRoute()}
                            <Route
                                path="/report-events"
                                element={
                                    <PrivateRoute>
                                        <ReportEventsComponent />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/aol"
                                element={
                                    <PrivateRoute>
                                        <AolComponent />
                                    </PrivateRoute>
                                }
                            />
                            {CargoTempLogRoute()}
                            {EnableCargoConsumablesRoute()}
                            {EnableVerificationRoute()}
                            {PBIReportRoute()}
                            {StowageRoute()}
                            {BunkeringRoute()}
                        </Routes>
                    </ContentWrapper>
                    {visibleSidePanelModuleIndex !== SidePanelModulEnum.NONE && <SidePanelPopup />}
                </PageWrapper>
            )}
        </AppWrapper>
    );
};

export default RouterContent;
